import { faCloudUpload } from "@fortawesome/pro-regular-svg-icons/faCloudUpload";
import { faPresentationScreen } from "@fortawesome/pro-solid-svg-icons/faPresentationScreen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Button, Card, CardContent, Grid, IconButton, Skeleton, Stack, Typography } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcConfirmDialog, MprcContainer, MprcIconButtonMoreOptionsDropdown, MprcJoinComponents, MprcLink, MprcRequestErrorReport, MprcRequestIndicator, MprcSpinnerIndicator, MprcText } from "components";
import { IMenuItem } from "components/MprcButtonDropdown";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { MprcBulletSeparator } from "components/MprcJoinComponents";
import { MprcSwitchPlayback } from "components/MprcSwitchPlayback";
import { FeedAttachDto, FeedContentLoadStatus, FeedItemDto, PublicationAttachType, TrainingActivityItemDto } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { Formaters } from "lib/Formaters";
import { enqueueSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useModalUserProfile } from "providers/UserProfileProvider";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SessionService } from "services/sessionService";
import { NIL } from "uuid";
import { CardOwnPropsOptions } from "views/knowledge/components/card";
import { BuildLigthBoxSlides, CardLightBox } from "views/knowledge/components/card/behaviors/common/CardLightbox";
import { InternalContentMedia } from "views/knowledge/components/card/behaviors/common/ContentMedia";
import { InternalAttach } from "views/knowledge/components/card/behaviors/common/DefaultAttach";
import FeedItemHelper from "views/knowledge/components/card/FeedItemHelper";
import { PrepareCardOwnProps } from "views/knowledge/components/card/PrepareCardOwnProps";
import { useEnrollmentViewContext } from "../../..";
import { ActivityDeliveryDrawer } from "./ActivityDeliveryDrawer";

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    enrollmentId?: string;
    componentId: string;
    readOnly?: boolean;
    isMultipleComponentsView?: boolean;
    onComplete: () => void;
    onPresentationMedia?: (attachType: PublicationAttachType) => void;
    onAttachOpen?: () => void;
    onAttachClose?: () => void;
}

/////////////////////////////////////////////////////////////////////////////////////

export const ActivityContent = (props: IProps) => {

    const { enrollmentId, componentId, isMultipleComponentsView, onAttachClose, onAttachOpen, onPresentationMedia, readOnly } = props;

    const enrollmentViewContext = useEnrollmentViewContext();
    const videoSearch = enrollmentViewContext?.videoSearch;

    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const { showUser } = useModalUserProfile();

    const [publication, setPublication] = useState<TrainingActivityItemDto>();
    const [cardOwnProps, setCardOwnProps] = useState<CardOwnPropsOptions>();
    const [loading, setLoading] = useState<boolean>(true);
    const [removing, setRemoving] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();
    const [dialog, setDialog] = useState<any>();

    const [localContent, setLocalContent] = useState<string>("");
    const [loadingContent, setLoadingContent] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<boolean>(false);
    const [openLightbox, setOpenLightbox] = useState<boolean>(false);
    const feedItemHelper = useRef<FeedItemHelper | undefined>(undefined);

    const currentMedia = useRef<FeedAttachDto | null>(null);
    const firstPlay = useRef<boolean>(true);

    const slides = useMemo(() => (
        publication && publication.presentation && publication.attachs
            ? BuildLigthBoxSlides(publication.presentation, publication.attachs, publication.presentation.rotationInterval == 0)
            : []
    ), [publication]);


    /////////////////////////////////////////////////////////////////////////////////////

    ///
    const loadContent = (publicationId: string) => {
        setLoadingContent(true);
        api.v1.knowledgePublication.getContent({ publicationId: publicationId })
            .then((result) => {
                setLocalContent(result.data);
                setExpanded(true);
                setPublication(state => ({ ...state!, contentLoadStatus: FeedContentLoadStatus.AllContentLoaded }));
            })
            .catch((e) => console.error(e))
            .finally(() => setLoadingContent(false));
    };

    ///
    const toogleStatus = () => {
        if (publication!.contentLoadStatus === FeedContentLoadStatus.AllContentLoaded) {
            setExpanded(!expanded);
        }
        else if (publication!.contentLoadStatus === FeedContentLoadStatus.LoadContentInDemand) {
            loadContent(publication!.publicationId);
        }
    };

    ///
    const handleOpenAttach = (attachId: string) => {
        if (onAttachOpen) onAttachOpen();
    };

    ///
    const handleCloseAttach = (attachId: string) => {
        if (onAttachClose) onAttachClose();
    };

    ///
    const handleDelivery = () => {
        setDialog(
            <ActivityDeliveryDrawer
                enrollmentId={enrollmentId!}
                componentId={componentId}
                maxFileSize={publication?.maxFileSize}
                onClose={
                    (reload) => {
                        setDialog(null);
                        if (reload) setLoading(true);
                    }
                }
            />
        );
    };

    const handleFooterAction = (option: string) => {
        switch (option) {
            case "download":
                SessionService.downloadWithToken(`/api/v1/enrollment/downloadActivity?enrollmentId=${enrollmentId}&componentId=${componentId}`);
                break;

            case "deliver":
                handleDelivery();
                break;

            case "remove":
                setDialog(<MprcConfirmDialog
                    open
                    message={t("Student.EnrollmentView.Content.Activity.ConfirmRemoveFile")}
                    onClose={(result) => { setDialog(null); setRemoving(result === true); }}
                />);
                break;
        }
    };

    /////////////////////////////////////////////////////////////////////////////////////
    /// MEDIA
    /////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        currentMedia.current = publication?.attachs?.find(p => p.attachId == cardOwnProps?.presentation.currentAttachId) ?? null;
        firstPlay.current = true;

        //
        if (currentMedia.current && onPresentationMedia) {
            onPresentationMedia(currentMedia.current.attachType);
        }

    }, [cardOwnProps]);

    useEffect(() => {
        setCardOwnProps((state) => {
            const newState = { ...state!, search: videoSearch } as CardOwnPropsOptions;
            return newState;
        });
    }, [videoSearch]);

    /////////////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    /////////////////////////////////////////////////////////////////////////////////////

    useDidUpdateEffect(() => {
        setLoading(true);
    }, [enrollmentId, componentId]);

    useEffect(() => {
        async function getData() {
            try {
                var response;
                if (enrollmentId) {
                    response = await api.v1.enrollment.getActivityItem({
                        componentId,
                        enrollmentId: enrollmentId == NIL ? null : enrollmentId,
                        classroomId: enrollmentId == NIL ? enrollmentViewContext.enrollment.classroomId : null
                    });
                }
                else {
                    response = await api.v1.lmsPublication.getActivityItem({ componentId });
                }

                feedItemHelper.current = new FeedItemHelper(response.data as unknown as FeedItemDto);
                setCardOwnProps(PrepareCardOwnProps(response.data as unknown as FeedItemDto, feedItemHelper.current, videoSearch));
                setPublication(response.data);
            } catch (error) {
                setViewError(error);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) getData();
    }, [loading]);

    useEffect(() => {
        async function remove() {
            try {
                const result = await api.v1.enrollment.removeDeliveredActivity({ enrollmentId: enrollmentId!, componentId });
                if (result) {
                    enqueueSnackbar(t("Student.EnrollmentView.Content.Activity.RemoveFileSuccess"), { variant: "success" });
                    setLoading(true);
                }
                else {
                    enqueueSnackbar(t("Student.EnrollmentView.Content.Activity.RemoveFileFailed"), { variant: "error" });
                }
            } catch (error) {
                setViewError(error);
            }
            finally {
                setRemoving(false);
            }
        }

        if (removing) remove();
    }, [removing]);

    /////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    /////////////////////////////////////////////////////////////////////////////////////

    const RenderHeader = () => {
        if (publication?.dueDate == null)
            return null;

        if (publication.expired)
            return (
                <Typography variant="captionHeader" color="error.main">
                    <MprcJoinComponents separatorVariant="bullet"
                        components={[
                            t("Student.EnrollmentView.Content.Activity.DeliveryDelayedText"),
                            <span>{t("Student.EnrollmentView.Content.Activity.ExpiredOnText")} {Formaters.performaDate(publication.dueDate)}</span>
                        ]}
                    />
                </Typography >
            );

        if (publication.enrollmentData?.deliveryDate != null && publication.enrollmentData?.scorePending) {
            return (
                <Typography variant="captionHeader" color="information.main">
                    <MprcJoinComponents
                        separatorVariant="bullet"
                        components={[
                            <span>{t("Student.EnrollmentView.Content.Activity.DeliveryAtText")} {Formaters.performaDate(publication.enrollmentData.deliveryDate)}</span>,
                            t("Student.EnrollmentView.Content.Activity.ScorePendingText")
                        ]}
                    />
                </Typography>
            );
        }

        if (publication.enrollmentData?.deliveryDate != null && publication.enrollmentData?.correctedOn != null) {
            return (
                <>
                    {isMultipleComponentsView &&
                        <Typography variant="captionHeader" color="success.main">
                            {t("Student.EnrollmentView.Content.Activity.ScoreText")}:
                            &nbsp;
                            {Formaters.percentString(publication.enrollmentData.scorePercent ?? 0, 1)}
                        </Typography>
                    }
                    <Typography variant="captionHeader" color="muted.main">
                        {isMultipleComponentsView && <MprcBulletSeparator />}
                        <MprcJoinComponents
                            separatorVariant="bullet"
                            components={[
                                <span>{t("Student.EnrollmentView.Content.Activity.DeliveryAtText")} {Formaters.performaDate(publication.enrollmentData.deliveryDate)}</span>,
                                <span>{t("Student.EnrollmentView.Content.Activity.CorrectedOnText")} {Formaters.performaDate(publication.enrollmentData.correctedOn)}</span>
                            ]}
                        />
                    </Typography>
                </>
            );
        }

        return (
            <Typography variant="captionHeader" color="warning.main">
                {t("Student.EnrollmentView.Content.Activity.DueDateText")}:
                &nbsp;
                {Formaters.performaDate(publication.dueDate, { dayOfWeek: false, preposition: "none" })}
                {
                    publication.showDaysLeft &&
                    <b>&nbsp;({t("Student.EnrollmentView.Content.Activity.HeaderDaysLeft", { count: publication.daysLeft! })})</b>
                }
            </Typography>
        );

    };

    const RenderFooter = () => {

        if (!publication?.digitalDelivery)
            return null;

        const canDelivery = !readOnly && (publication.canUploadFile || publication.canReplaceFile);

        const contextMenu: IMenuItem[] = [];
        if (publication.canDownloadFile) {
            contextMenu.push({ value: "download", text: t("Student.EnrollmentView.Content.Activity.Actions.DownloadText") });
            if (!readOnly && publication.canDeleteFile) contextMenu.push({ value: "remove", text: <MprcText color="error">{t("Student.EnrollmentView.Content.Activity.Actions.RemoveText")}</MprcText> });
        }

        return (
            <Stack direction="row" justifyContent="space-between">

                <Stack direction="row" spacing={1} alignItems="center">
                    {
                        canDelivery &&
                        <>
                            <FontAwesomeIcon icon={faCloudUpload} fixedWidth />
                            <Button variant="text" onClick={handleDelivery} color="muted">
                                {
                                    publication.canUploadFile
                                        ? t("Student.EnrollmentView.Content.Activity.UploadText")
                                        : t("Student.EnrollmentView.Content.Activity.ReplaceUploadText")
                                }
                            </Button>
                        </>
                    }
                </Stack>
                {
                    contextMenu.length > 0 &&
                    <MprcIconButtonMoreOptionsDropdown items={contextMenu} onChange={handleFooterAction} />
                }
            </Stack>
        );
    };

    return (
        <>
            <MprcRequestIndicator type="post" visible={removing} />

            {
                loading
                    ? <Skeleton variant="rectangular" width="100%" height={"500px"} />
                    : viewError
                        ? <MprcRequestErrorReport error={viewError} />
                        : publication &&
                        <MprcContainer margin={enrollmentId ? "normal" : "none"}>
                            {dialog}
                            <Card elevation={!enrollmentId ? 0 : undefined}>
                                <CardContent sx={{ p: 1, pb: 0 }}>
                                    <Grid container width="100%" direction="column" gap={1}>
                                        <Grid id="headerSection" width="100%">
                                            {RenderHeader()}
                                        </Grid>
                                        <Grid id="mediaHeaderSection" width="100%">
                                            <InternalContentMedia
                                                publicationId={publication.publicationId}
                                                presentation={publication.presentation}
                                                attachs={publication.attachs}
                                                cardOwnProps={cardOwnProps!}
                                                onUpdateCardOwnProps={(data: Partial<CardOwnPropsOptions>) =>
                                                    setCardOwnProps((state) => {
                                                        const newState = { ...state!, ...data };
                                                        return newState;
                                                    })
                                                }
                                            />
                                            {
                                                feedItemHelper.current != undefined &&
                                                (feedItemHelper.current.hasAutoPlayPresentation || feedItemHelper.current.hasPresentation) &&
                                                <Grid container spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
                                                    {
                                                        feedItemHelper.current.hasAutoPlayPresentation &&
                                                        <Grid>
                                                            <MprcSwitchPlayback
                                                                checked={cardOwnProps!.presentation.autoplay}
                                                                onChange={
                                                                    (_, checked) => setCardOwnProps({ ...cardOwnProps!, presentation: { ...cardOwnProps!.presentation, autoplay: checked } })
                                                                }
                                                            />
                                                        </Grid>
                                                    }
                                                    {
                                                        feedItemHelper.current.hasPresentation && publication.presentation &&
                                                        <Grid>
                                                            <Typography variant="iconText">
                                                                <IconButton color="inherit"
                                                                    onClick={() => {
                                                                        setOpenLightbox(true);
                                                                        setCardOwnProps(state => ({ ...state!, presentation: { ...state!.presentation, lighboxOpened: true } }));
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faPresentationScreen} color="inherit" />
                                                                </IconButton>
                                                            </Typography>
                                                            <CardLightBox
                                                                slides={slides}
                                                                open={openLightbox}
                                                                index={0}
                                                                autoplay={publication.presentation.rotationInterval > 0}
                                                                rotationInterval={publication.presentation.rotationInterval > 0 ? publication.presentation!.rotationInterval : 5}
                                                                onClose={() => {
                                                                    setOpenLightbox(false);
                                                                    setCardOwnProps(state => ({ ...state!, presentation: { ...state!.presentation, lighboxOpened: false } }));
                                                                }}
                                                            />
                                                        </Grid>
                                                    }
                                                </Grid>
                                            }
                                        </Grid>
                                        <Grid width="100%">
                                            <Grid container gap={1}>
                                                <Grid size="grow" container direction="column" gap={1} width={"100%"}>
                                                    <Grid id="titleSection" width="100%">
                                                        <MprcFeedTypography variant="title" component="div" noWrap color="black">
                                                            <span title={publication.title}>{publication.title}</span>
                                                        </MprcFeedTypography>
                                                    </Grid>
                                                    <Grid width="100%">
                                                        <Grid container justifyContent="center" alignItems="center" spacing={1} wrap="nowrap">
                                                            <Grid>
                                                                <Avatar
                                                                    className={AppGlobalClassKeys.clickable}
                                                                    src={publication.author.thumbnailUrl}
                                                                    onClick={() => showUser(publication.author.userId)}
                                                                />

                                                            </Grid>
                                                            <Grid size="grow">
                                                                <MprcLink onClick={() => showUser(publication.author.userId)} variant="body2" fontWeight="bold">
                                                                    <Typography variant="body2" fontWeight="bold" noWrap>
                                                                        {publication.author.name}
                                                                    </Typography>
                                                                </MprcLink>
                                                                {publication.publishedOn &&
                                                                    <MprcFeedTypography variant="authorSecondary" component="div">
                                                                        <span>{t("knowledge:Feed.Card.Common.PublishOnText")} {Formaters.relativeTime(publication.publishedOn)}</span>
                                                                        {publication.editedOn && <span> {t("knowledge:Feed.Card.Common.EditedText", { date: Formaters.relativeTime(publication.editedOn) })}</span>}
                                                                    </MprcFeedTypography>
                                                                }
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid width="100%">
                                                        <Grid container gap={1} justifyContent="center">
                                                            <Grid id="contentSection" className={AppGlobalClassKeys.qlContentSection} width="100%">
                                                                <MprcFeedTypography variant="content" component="div">
                                                                    <div dangerouslySetInnerHTML={{
                                                                        __html: !expanded || publication.contentLoadStatus == FeedContentLoadStatus.NoActionNecessary
                                                                            ? publication.content ?? ""
                                                                            : localContent
                                                                    }}></div>
                                                                </MprcFeedTypography>
                                                                {
                                                                    publication.contentLoadStatus !== FeedContentLoadStatus.NoActionNecessary &&
                                                                    <>
                                                                        {
                                                                            loadingContent
                                                                                ? <MprcSpinnerIndicator />
                                                                                : <MprcFeedTypography variant="moreContent" component="a" onClick={() => toogleStatus()}>
                                                                                    {expanded ? t("knowledge:Feed.Card.Common.SeeLessText") : t("knowledge:Feed.Card.Common.SeeMoreText")}
                                                                                </MprcFeedTypography>
                                                                        }
                                                                    </>
                                                                }
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid id="attachSection" width="100%">
                                            <InternalAttach
                                                publicationId={publication.publicationId}
                                                attachs={publication.attachs}
                                                helper={feedItemHelper.current!}
                                                onOpen={handleOpenAttach}
                                                onClose={handleCloseAttach}
                                            />
                                        </Grid>
                                        {enrollmentId && enrollmentId != NIL &&
                                            <Grid id="footerSection" width="100%">
                                                {RenderFooter()}
                                            </Grid>
                                        }
                                    </Grid>
                                </CardContent>
                            </Card>
                        </MprcContainer>
            }
        </>
    );
};
